/**
 * hooks/useMonth.ts
 * Month selection helper for Gregorian months, with utilities to derive ranges.
 */

import { useEffect, useMemo } from 'react'
import { useStringPref } from './usePref'

/**
 * formatMonth
 * Ensures YYYY-MM format for a Date.
 */
export function formatMonth(d: Date) {
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, '0')
  return `${y}-${m}`
}

/**
 * isValidMonthStr
 * Returns true if value matches YYYY-MM and month is 01..12.
 */
export function isValidMonthStr(s: string) {
  return /^\d{4}-(0[1-9]|1[0-2])$/.test(s)
}

/**
 * getMonthRange
 * Returns start (ISO) at 00:00:00 and end (ISO) at 23:59:59.999 for the given month (YYYY-MM).
 */
export function getMonthRange(month: string) {
  const [yStr, mStr] = month.split('-')
  const y = Number(yStr)
  const m = Number(mStr) - 1
  const start = new Date(y, m, 1, 0, 0, 0, 0)
  const end = new Date(y, m + 1, 0, 23, 59, 59, 999)
  return { start, end, startISO: start.toISOString(), endISO: end.toISOString() }
}

/**
 * useSelectedMonth
 * Keeps a selected Gregorian month in localStorage and exposes its range.
 * - Auto-corrects invalid stored value to current month to avoid RangeError.
 */
export function useSelectedMonth() {
  const current = formatMonth(new Date())
  const [month, setMonth] = useStringPref('selectedMonth', current)

  // Auto-correct invalid month values from storage
  useEffect(() => {
    if (!isValidMonthStr(month)) {
      setMonth(current)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [month])

  const range = useMemo(() => getMonthRange(isValidMonthStr(month) ? month : current), [month])

  const next = () => {
    const [yStr, mStr] = (isValidMonthStr(month) ? month : current).split('-')
    const y = Number(yStr)
    const m = Number(mStr)
    const d = new Date(y, m - 1, 1)
    d.setMonth(d.getMonth() + 1)
    setMonth(formatMonth(d))
  }

  const prev = () => {
    const [yStr, mStr] = (isValidMonthStr(month) ? month : current).split('-')
    const y = Number(yStr)
    const m = Number(mStr)
    const d = new Date(y, m - 1, 1)
    d.setMonth(d.getMonth() - 1)
    setMonth(formatMonth(d))
  }

  return { month, setMonth, range, next, prev }
}
